import React, { useState, useRef, useEffect, ReactNode, useCallback } from 'react';

import { ControlButtonsAndDots } from './ControlButtonsAndDots';

// 1. Define the props interface
interface IHorizontalPaginationProps {
  classOuter?: string;
  classIner?: string;
  itemWidth?: number; // Optional, default to 100
  scale?: number; //Optional, default to 1
  bringToViewIndex?: number;
  children: ReactNode; // Any valid React children
}

const HorizontalPagination: React.FC<IHorizontalPaginationProps> = ({
  classOuter = 'flex flex-col items-center overflow-hidden gap-1',
  classIner = 'flex overflow-x-hidden scroll-smooth p-2 w-full gap-3',
  itemWidth = 100,
  bringToViewIndex,
  children,
}) => {
  // 2. Type state variables
  const [currentPage, setCurrentPage] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null); // 3. Type useRef
  const [itemsPerPage, setItemsPerPage] = useState<number>(0);

  // Get the total number of items from children
  const totalItems: number = React.Children.count(children);
  useEffect(() => {
    setCurrentPage(0);
  }, [children]);

  // Calculate items per page based on container width
  useEffect(() => {
    const calculateItemsPerPage = (): void => {
      if (containerRef.current) {
        const containerWidth: number = containerRef.current.offsetWidth;
        setItemsPerPage(Math.max(1, Math.floor(containerWidth / itemWidth)));
      }
    };

    calculateItemsPerPage();
    // Add event listener for window resize
    window.addEventListener('resize', calculateItemsPerPage);
    // Cleanup function to remove event listener

    return () => window.removeEventListener('resize', calculateItemsPerPage);
  }, [itemWidth]); // Recalculate if itemWidth changes

  // Total number of pages
  const totalPages: number = itemsPerPage > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  // Function to scroll to a specific page
  const scrollToPage = useCallback(
    (pageIndex: number): void => {
      if (containerRef.current && itemsPerPage > 0) {
        const scrollPosition: number = pageIndex * itemsPerPage * itemWidth;
        containerRef.current.scrollLeft = scrollPosition;
      }
    },
    [itemWidth, itemsPerPage],
  );

  useEffect(() => {
    if (bringToViewIndex && bringToViewIndex > 0 && itemsPerPage > 0) {
      const locatedAtPage = Math.min(Math.floor(bringToViewIndex / itemsPerPage), totalPages - 1);
      setCurrentPage((v) => {
        if (v !== locatedAtPage) {
          scrollToPage(locatedAtPage);
        }

        return locatedAtPage;
      });
    }
  }, [bringToViewIndex, itemsPerPage, scrollToPage, totalPages]);

  const handlePrev = useCallback((): void => {
    setCurrentPage((prevPage) => {
      const newPage = Math.max(0, prevPage - 1);
      scrollToPage(newPage);

      return newPage;
    });
  }, [scrollToPage]);

  const handleNext = useCallback((): void => {
    setCurrentPage((prevPage) => {
      const newPage = Math.min(totalPages - 1, prevPage + 1);
      scrollToPage(newPage);

      return newPage;
    });
  }, [scrollToPage, totalPages]);

  return (
    <div className={classOuter}>
      {/* Scrollable Container for Children */}
      <div ref={containerRef} className={classIner}>
        {/* Render children, ensuring each child has the specified itemWidth */}
        {React.Children.map(
          children,
          (
            child: ReactNode,
            index: number, // Type 'child' as ReactNode
          ) => {
            return (
              <div className="flex-shrink-0" style={{ width: itemWidth }}>
                {child}
              </div>
            );
          },
        )}
      </div>
      {/* Control Buttons and Dots */}
      <ControlButtonsAndDots
        currentPage={currentPage}
        totalPages={totalPages}
        handlePrev={handlePrev}
        jumpto={(index) => {
          setCurrentPage(index);
          scrollToPage(index);
        }}
        handleNext={handleNext}
      />
    </div>
  );
};

export default HorizontalPagination;
